import { useEffect, useRef } from 'react';

interface UseTabVisibilityOptions {
  onBecomeVisible?: () => void;
  debounceMs?: number;
  triggerOnMount?: boolean;
}

/**
 * Hook to detect when the browser tab becomes visible/focused
 */
export const useTabVisibility = (options: UseTabVisibilityOptions = {}) => {
  const { onBecomeVisible, debounceMs = 300, triggerOnMount = true } = options;
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (!onBecomeVisible) {
      return;
    }

    const triggerCallback = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        onBecomeVisible();
      }, debounceMs);
    };

    const handleVisibilityChange = () => {
      // Only trigger when tab becomes visible
      if (document.visibilityState === 'visible') {
        triggerCallback();
      }
    };
    const handleWindowFocus = () => {
      triggerCallback();
    };
    if (triggerOnMount && document.visibilityState === 'visible') {
      triggerCallback();
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    // iOS Safari: pageshow fires when returning from back/forward cache or app switching
    const handlePageShow = () => {
      triggerCallback();
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      // Cleanup timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      // Remove event listeners
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [onBecomeVisible, debounceMs, triggerOnMount]);
  return {
    isVisible: document.visibilityState === 'visible'
  };
};
